/**
 * Header interactions:
 * - Search panel toggle + autocomplete
 * - Login panel toggle + tab switching (login / register / lost pwd)
 * - Language switcher dropdown
 * - Mobile nav drawer
 *
 * All dropdowns close on outside click or Escape key.
 * Uses lbhAccommodations (window global injected by wp_localize_script) for autocomplete.
 */

import { gtm } from './gtm.js';

// ---------------------------------------------------------------------------
// Generic dropdown helper
// ---------------------------------------------------------------------------
function initDropdown( toggleEl, panelEl ) {
    if ( ! toggleEl || ! panelEl ) return;

    function open() {
        toggleEl.setAttribute( 'aria-expanded', 'true' );
        panelEl.removeAttribute( 'aria-hidden' );
        panelEl.removeAttribute( 'hidden' );
    }

    function close() {
        toggleEl.setAttribute( 'aria-expanded', 'false' );
        panelEl.setAttribute( 'aria-hidden', 'true' );
    }

    toggleEl.addEventListener( 'click', ( e ) => {
        e.stopPropagation();
        const isOpen = toggleEl.getAttribute( 'aria-expanded' ) === 'true';
        isOpen ? close() : open();
    } );

    document.addEventListener( 'click', ( e ) => {
        if ( ! toggleEl.closest( '[data-search],[data-login],[data-lang],[data-user]' )?.contains( e.target ) ) {
            close();
        }
    } );

    document.addEventListener( 'keydown', ( e ) => {
        if ( e.key === 'Escape' ) close();
    } );
}


// ---------------------------------------------------------------------------
// Custom select dropdowns
// ---------------------------------------------------------------------------
function initCustomSelects() {
    document.querySelectorAll( '[data-custom-select]' ).forEach( wrap => {
        const trigger = wrap.querySelector( '.lbh-custom-select__trigger' );
        const list    = wrap.querySelector( '.lbh-custom-select__list' );
        const valueEl = wrap.querySelector( '.lbh-custom-select__value' );
        const options = wrap.querySelectorAll( '.lbh-custom-select__option' );

        if ( ! trigger || ! list ) return;

        function openSelect() {
            trigger.setAttribute( 'aria-expanded', 'true' );
            list.classList.add( 'is-open' );
        }

        function closeSelect() {
            trigger.setAttribute( 'aria-expanded', 'false' );
            list.classList.remove( 'is-open' );
        }

        trigger.addEventListener( 'click', e => {
            e.stopPropagation();
            const isOpen = trigger.getAttribute( 'aria-expanded' ) === 'true';

            // close all other custom selects first
            document.querySelectorAll( '[data-custom-select]' ).forEach( other => {
                if ( other !== wrap ) {
                    other.querySelector( '.lbh-custom-select__trigger' )?.setAttribute( 'aria-expanded', 'false' );
                    other.querySelector( '.lbh-custom-select__list' )?.classList.remove( 'is-open' );
                }
            } );

            isOpen ? closeSelect() : openSelect();
        } );

        options.forEach( opt => {
            opt.addEventListener( 'click', () => {
                const url   = opt.dataset.value;
                const label = opt.textContent.trim();

                if ( valueEl ) valueEl.textContent = label;

                options.forEach( o => o.classList.remove( 'is-selected' ) );
                if ( ! opt.classList.contains( 'is-placeholder' ) ) {
                    opt.classList.add( 'is-selected' );

                    // GTM tracking
                    const selectType = wrap.dataset.selectType || '';
                    const gtmId      = opt.dataset.gtmId   || '';
                    const gtmName    = opt.dataset.gtmName || label;

                    if ( selectType === 'destination' ) {
                        gtm.searchDestination( { destination_name: gtmName, destination_id: gtmId } );
                    } else if ( selectType === 'region' ) {
                        gtm.searchRegion( { region_name: gtmName, region_id: gtmId } );
                    }
                }

                closeSelect();

                if ( url ) window.location.href = url;
            } );
        } );

        document.addEventListener( 'click', e => {
            if ( ! wrap.contains( e.target ) ) closeSelect();
        } );

        document.addEventListener( 'keydown', e => {
            if ( e.key === 'Escape' ) closeSelect();
        } );
    } );
}


// ---------------------------------------------------------------------------
// Search panel
// ---------------------------------------------------------------------------
function initSearch() {
    const wrap   = document.querySelector( '[data-search]' );
    if ( ! wrap ) return;

    const toggle = wrap.querySelector( '.lbh-search__toggle' );
    const panel  = wrap.querySelector( '.lbh-search__panel' );
    initDropdown( toggle, panel );

    initCustomSelects();

    // Autocomplete
    const input       = wrap.querySelector( '.lbh-search__input' );
    const suggestions = wrap.querySelector( '.lbh-search__suggestions' );
    const posts       = window.lbhSearchData || [];

    if ( ! input || ! suggestions || ! posts.length ) return;

    input.addEventListener( 'input', () => {
        const query = input.value.trim().toLowerCase();
        suggestions.innerHTML = '';

        if ( query.length < 2 ) {
            suggestions.setAttribute( 'aria-hidden', 'true' );
            return;
        }

        const matches = posts.filter( p => p.title.toLowerCase().includes( query ) ).slice( 0, 8 );

        if ( ! matches.length ) {
            suggestions.setAttribute( 'aria-hidden', 'true' );
            return;
        }

        matches.forEach( p => {
            const li = document.createElement( 'li' );
            li.setAttribute( 'role', 'option' );
            li.innerHTML = `<span>${p.title}</span>`;
            li.addEventListener( 'click', () => {
                gtm.searchByName( { search_term: input.value.trim(), result_clicked: p.title } );
                window.location.href = p.permalink;
            } );
            suggestions.appendChild( li );
        } );

        suggestions.removeAttribute( 'aria-hidden' );
    } );

    input.addEventListener( 'keydown', ( e ) => {
        if ( e.key === 'Enter' ) {
            const first = suggestions.querySelector( 'li' );
            if ( first ) {
                gtm.searchByName( { search_term: input.value.trim(), result_clicked: first.textContent.trim() } );
                first.click();
            }
        }
    } );

    document.addEventListener( 'click', ( e ) => {
        if ( ! wrap.contains( e.target ) ) {
            suggestions.innerHTML = '';
            suggestions.setAttribute( 'aria-hidden', 'true' );
        }
    } );
}


// ---------------------------------------------------------------------------
// Login panel (tab switching: login / lostpwd / register)
// ---------------------------------------------------------------------------
function initLoginPanel() {
    const wrap = document.querySelector( '[data-login]' );
    if ( ! wrap ) return;

    const toggle = wrap.querySelector( '.lbh-login__toggle' );
    const panel  = wrap.querySelector( '.lbh-login__panel' );
    initDropdown( toggle, panel );

    // Tab switching
    wrap.addEventListener( 'click', ( e ) => {
        const switcher = e.target.closest( '[data-switch]' );
        if ( ! switcher ) return;

        const target = switcher.dataset.switch;
        wrap.querySelectorAll( '.lbh-login__block' ).forEach( block => {
            const isTarget = block.dataset.block === target;
            block.hidden       = ! isTarget;
            block.classList.toggle( 'is-active', isTarget );
        } );
    } );

    // Close buttons inside the panel
    wrap.querySelectorAll( '.lbh-login__close' ).forEach( btn => {
        btn.addEventListener( 'click', () => {
            toggle.setAttribute( 'aria-expanded', 'false' );
            panel.setAttribute( 'aria-hidden', 'true' );
        } );
    } );

    // AJAX form submission
    wrap.querySelectorAll( '.lbh-form' ).forEach( form => {
        form.addEventListener( 'submit', async ( e ) => {
            e.preventDefault();
            const status   = form.querySelector( '.lbh-form__status' );
            const action   = form.dataset.action;
            const nonce    = form.dataset.nonce;
            const lang     = form.dataset.lang;
            const redirect = form.dataset.redirect;

            const body = new FormData( form );
            body.set( 'action', action );
            body.set( 'security', nonce );
            body.set( 'a_lang', lang );

            // checkbox a_auth: convert to 'true'/'false' string
            const authBox = form.querySelector( '[name="a_auth"]' );
            if ( authBox ) body.set( 'a_auth', authBox.checked ? 'true' : 'false' );

            try {
                const res  = await fetch( window.lbhAjax?.url || '/wp-admin/admin-ajax.php', { method: 'POST', body } );
                const data = await res.json();
                if ( status ) status.innerHTML = data.message || '';
                if ( data.loggedin && redirect ) {
                    setTimeout( () => window.location.href = redirect, 800 );
                }
            } catch {
                if ( status ) status.textContent = 'Request failed.';
            }
        } );
    } );
}


// ---------------------------------------------------------------------------
// User dropdown (when logged in)
// ---------------------------------------------------------------------------
function initUserDropdown() {
    const wrap = document.querySelector( '[data-user]' );
    if ( ! wrap ) return;
    const toggle = wrap.querySelector( '.lbh-user__toggle' );
    const panel  = wrap.querySelector( '.lbh-user__dropdown' );
    initDropdown( toggle, panel );
}


// ---------------------------------------------------------------------------
// Language switcher
// ---------------------------------------------------------------------------
function initLangSwitcher() {
    const wrap = document.querySelector( '[data-lang]' );
    if ( ! wrap ) return;
    const toggle = wrap.querySelector( '.lbh-lang__toggle' );
    const panel  = wrap.querySelector( '.lbh-lang__list' );
    initDropdown( toggle, panel );
}


// ---------------------------------------------------------------------------
// Mobile navigation drawer
// ---------------------------------------------------------------------------
function initMobileNav() {
    const hamburger = document.querySelector( '.lbh-hamburger' );
    const drawer    = document.getElementById( 'lbh-mobile-nav' );
    const close     = drawer?.querySelector( '.lbh-mobile-nav__close' );
    const overlay   = drawer?.querySelector( '.lbh-mobile-nav__overlay' );

    if ( ! hamburger || ! drawer ) return;

    function openDrawer() {
        drawer.setAttribute( 'aria-hidden', 'false' );
        drawer.classList.add( 'is-open' );
        hamburger.setAttribute( 'aria-expanded', 'true' );
        document.body.classList.add( 'lbh-nav-open' );
    }

    function closeDrawer() {
        drawer.setAttribute( 'aria-hidden', 'true' );
        drawer.classList.remove( 'is-open' );
        hamburger.setAttribute( 'aria-expanded', 'false' );
        document.body.classList.remove( 'lbh-nav-open' );
    }

    hamburger.addEventListener( 'click', openDrawer );
    close?.addEventListener( 'click', closeDrawer );
    overlay?.addEventListener( 'click', closeDrawer );
    document.addEventListener( 'keydown', ( e ) => {
        if ( e.key === 'Escape' ) closeDrawer();
    } );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
export function initHeader() {
    initSearch();
    initLoginPanel();
    initUserDropdown();
    initLangSwitcher();
    initMobileNav();
}
